/** Redis/import JSON may store explicit null; spread merges leave null and break `.trim()`. */
export function nz(v: unknown): string {
  return v == null ? "" : String(v);
}

export function emailsMatch(a: string, b: string): boolean {
  return nz(a).trim().toLowerCase() === nz(b).trim().toLowerCase();
}

export type HarvestUserRow = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  baseline_weekly_hours?: string;
};

export type HarvestProjectRow = {
  id: string;
  name: string;
  budget: string;
  budget_spent: string;
  /** Opening balance before scenario time entries; set by recalc, cleared on manual budget_spent edit */
  budget_spent_anchor?: string;
  starts_on: string;
};

export type BambooEmployeeRow = {
  id: string;
  workEmail: string;
  displayName: string;
  firstName: string;
  lastName: string;
};

export type ScenarioEmployeesSlice = {
  harvest: { users: HarvestUserRow[] };
  bamboo: { employees: BambooEmployeeRow[] };
};

export type ScenarioWithStableIds = ScenarioEmployeesSlice & {
  harvest: ScenarioEmployeesSlice["harvest"] & {
    projects: HarvestProjectRow[];
    time_entries: Array<{
      user_harvest_id: string;
      project_harvest_id: string;
      spent_date: string;
      hours: string;
    }>;
  };
};

export type HarvestTimeEntryRow = {
  user_harvest_id: string;
  project_harvest_id: string;
  spent_date: string;
  hours: string;
};

export type TimeEntryMatrixRow = {
  rowKey: string;
  user_harvest_id: string;
  project_harvest_id: string;
  hoursByDate: Record<string, string>;
};

function parseIsoDate(iso: string): Date | null {
  const s = nz(iso).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

function isoFromUtcDate(dt: Date): string {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatHoursInput(v: number): string {
  if (!Number.isFinite(v)) return "";
  if (Math.abs(v) < 1e-9) return "0";
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(2)));
}

export function newTimeEntryMatrixRowKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `time-entry-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function buildWeekDates(weekStartIso: string): string[] {
  const start = parseIsoDate(weekStartIso);
  if (!start) return [];
  return [ 0, 1, 2, 3, 4 ].map((offset) => {
    const dt = new Date(start.getTime());
    dt.setUTCDate(start.getUTCDate() + offset);
    return isoFromUtcDate(dt);
  });
}

export function emptyTimeEntryMatrixRow(weekDates: string[]): TimeEntryMatrixRow {
  const hoursByDate = Object.fromEntries(weekDates.map((d) => [ d, "" ]));
  return {
    rowKey: newTimeEntryMatrixRowKey(),
    user_harvest_id: "",
    project_harvest_id: "",
    hoursByDate,
  };
}

/** Random Mon–Fri-style split: total hours ≤ maxTotalHours (default 40), half-hour granularity. */
export function randomWeekHoursUnderCap(
  weekDates: string[],
  maxTotalHours = 40,
  rng: () => number = Math.random,
): Record<string, string> {
  const base = Object.fromEntries(weekDates.map((d) => [ d, "" ]));
  const n = weekDates.length;
  if (n === 0) return base;

  const maxHalf = Math.floor(maxTotalHours * 2);
  const minHalf = Math.min(24, maxHalf); // at least 12h weekly target when cap allows
  const totalHalf =
    maxHalf <= 0
      ? 0
      : maxHalf <= minHalf
        ? maxHalf
        : minHalf + Math.floor(rng() * (maxHalf - minHalf + 1));

  const units = Array.from({ length: n }, () => 0);
  for (let u = 0; u < totalHalf; u++) {
    units[Math.floor(rng() * n)]++;
  }

  for (let i = 0; i < n; i++) {
    const hours = units[i] * 0.5;
    base[weekDates[i]] = hours > 0 ? formatHoursInput(hours) : "";
  }
  return base;
}

export function timeEntriesToMatrixRows(
  timeEntries: HarvestTimeEntryRow[],
  weekDates: string[],
): TimeEntryMatrixRow[] {
  const weekDateSet = new Set(weekDates);
  const rows = new Map<string, TimeEntryMatrixRow>();
  for (const entry of timeEntries) {
    const spentDate = nz(entry.spent_date).trim();
    if (!weekDateSet.has(spentDate)) continue;
    const userId = nz(entry.user_harvest_id).trim();
    const projectId = nz(entry.project_harvest_id).trim();
    if (!userId || !projectId) continue;
    const groupKey = `${userId}::${projectId}`;
    if (!rows.has(groupKey)) {
      rows.set(groupKey, {
        rowKey: newTimeEntryMatrixRowKey(),
        user_harvest_id: userId,
        project_harvest_id: projectId,
        hoursByDate: Object.fromEntries(weekDates.map((d) => [ d, "" ])),
      });
    }
    const row = rows.get(groupKey)!;
    const nextHours = (Number.parseFloat(row.hoursByDate[spentDate] || "0") || 0)
      + (Number.parseFloat(nz(entry.hours).trim()) || 0);
    row.hoursByDate[spentDate] = formatHoursInput(nextHours);
  }
  return Array.from(rows.values());
}

export function matrixRowsToTimeEntries(
  matrixRows: TimeEntryMatrixRow[],
  weekDates: string[],
): HarvestTimeEntryRow[] {
  const out: HarvestTimeEntryRow[] = [];
  for (const row of matrixRows) {
    const userId = nz(row.user_harvest_id).trim();
    const projectId = nz(row.project_harvest_id).trim();
    if (!userId || !projectId) continue;
    for (const spentDate of weekDates) {
      const rawHours = nz(row.hoursByDate[spentDate]).trim();
      if (!rawHours) continue;
      const parsed = Number.parseFloat(rawHours);
      out.push({
        user_harvest_id: userId,
        project_harvest_id: projectId,
        spent_date: spentDate,
        hours: String(Number.isFinite(parsed) ? parsed : 0),
      });
    }
  }
  return out;
}

export function updateMatrixCell(
  rows: TimeEntryMatrixRow[],
  rowKey: string,
  spentDate: string,
  value: string,
): TimeEntryMatrixRow[] {
  return rows.map((row) => {
    if (row.rowKey !== rowKey) return row;
    return {
      ...row,
      hoursByDate: {
        ...row.hoursByDate,
        [spentDate]: value,
      },
    };
  });
}

/** Stable UI order: display label by user, then by project, then raw ids. */
export function sortTimeEntryMatrixRows(
  rows: TimeEntryMatrixRow[],
  users: HarvestUserRow[],
  projects: HarvestProjectRow[],
): TimeEntryMatrixRow[] {
  const userSortKey = (id: string): string => {
    const u = users.find((x) => nz(x.id).trim() === nz(id).trim());
    return u ? harvestUserDisplayLabel(u) : nz(id).trim();
  };
  const projectSortKey = (id: string): string => {
    const p = projects.find((x) => nz(x.id).trim() === nz(id).trim());
    return p ? harvestProjectDisplayLabel(p) : nz(id).trim();
  };
  return [ ...rows ].sort((a, b) => {
    const ua = nz(a.user_harvest_id).trim();
    const ub = nz(b.user_harvest_id).trim();
    const pa = nz(a.project_harvest_id).trim();
    const pb = nz(b.project_harvest_id).trim();
    const cu = userSortKey(ua).localeCompare(userSortKey(ub), undefined, { sensitivity: "base" });
    if (cu !== 0) return cu;
    const cp = projectSortKey(pa).localeCompare(projectSortKey(pb), undefined, { sensitivity: "base" });
    if (cp !== 0) return cp;
    const iu = ua.localeCompare(ub);
    if (iu !== 0) return iu;
    return pa.localeCompare(pb);
  });
}

function maxNumericHarvestId(ids: Iterable<string>): number {
  let m = 0;
  for (const x of ids) {
    const n = Number.parseInt(nz(x).trim(), 10);
    if (Number.isFinite(n) && n > m) m = n;
  }
  return m;
}

/** Assigns numeric string ids to Harvest users, projects, and Bamboo rows missing ids (UI hides ids). */
export function ensureScenarioStableIds<T extends ScenarioWithStableIds>(input: T): T {
  const users = input.harvest.users.map((u) => ({ ...u }));
  const projects = input.harvest.projects.map((p) => ({ ...p }));
  const employees = input.bamboo.employees.map((e) => ({ ...e }));

  const existingIds: string[] = [];
  for (const u of users) if (nz(u.id).trim()) existingIds.push(u.id);
  for (const p of projects) if (nz(p.id).trim()) existingIds.push(p.id);
  for (const e of employees) if (nz(e.id).trim()) existingIds.push(e.id);

  let next = maxNumericHarvestId(existingIds) + 1;

  for (let i = 0; i < users.length; i++) {
    if (!nz(users[i].id).trim()) {
      users[i] = { ...users[i], id: String(next++) };
    }
  }

  const emailToUserId = new Map<string, string>();
  for (const u of users) {
    const em = nz(u.email).trim().toLowerCase();
    if (em) emailToUserId.set(em, u.id);
  }

  for (let i = 0; i < projects.length; i++) {
    if (!nz(projects[i].id).trim()) {
      projects[i] = { ...projects[i], id: String(next++) };
    }
  }

  for (let i = 0; i < employees.length; i++) {
    let id = nz(employees[i].id).trim();
    const email = nz(employees[i].workEmail).trim().toLowerCase();
    if (!id && email && emailToUserId.has(email)) {
      id = emailToUserId.get(email)!;
    } else if (!id) {
      id = String(next++);
    }
    employees[i] = { ...employees[i], id };
  }

  return {
    ...input,
    harvest: {
      ...input.harvest,
      users,
      projects,
    },
    bamboo: {
      ...input.bamboo,
      employees,
    },
  } as T;
}

/** Label for Harvest user selects (no raw id shown to humans). */
export function harvestUserDisplayLabel(u: HarvestUserRow): string {
  const name = [ nz(u.first_name), nz(u.last_name) ].filter((x) => nz(x).trim()).join(" ").trim();
  const email = nz(u.email).trim();
  if (name && email) return `${name} (${email})`;
  if (name) return name;
  return email || "User";
}

export function harvestProjectDisplayLabel(p: HarvestProjectRow): string {
  const n = nz(p.name).trim();
  return n || "Untitled project";
}

export function bambooEmployeeDisplayLabel(e: BambooEmployeeRow): string {
  const dn = nz(e.displayName).trim();
  const nameFromParts = [ nz(e.firstName), nz(e.lastName) ].filter((x) => nz(x).trim()).join(" ").trim();
  const name = dn || nameFromParts;
  const email = nz(e.workEmail).trim();
  if (name && email) return `${name} (${email})`;
  if (name) return name;
  return email || "Employee";
}

export function isPickableHarvestUser(u: HarvestUserRow): boolean {
  return Boolean(nz(u.email).trim() || nz(u.first_name).trim() || nz(u.last_name).trim());
}

export function isPickableHarvestProject(p: HarvestProjectRow): boolean {
  return Boolean(nz(p.id).trim());
}

export function isPickableBambooEmployee(e: BambooEmployeeRow): boolean {
  return Boolean(
    nz(e.workEmail).trim()
    || nz(e.displayName).trim()
    || nz(e.firstName).trim()
    || nz(e.lastName).trim(),
  );
}

/** Upserts Bamboo employees from Harvest users (id + email); matches existing rows by workEmail. */
function parseHoursValue(raw: string): number {
  const n = Number.parseFloat(nz(raw).trim());
  return Number.isFinite(n) ? n : 0;
}

function formatBudgetSpentHours(v: number): string {
  if (!Number.isFinite(v) || Math.abs(v) < 1e-9) return "";
  return formatHoursInput(v);
}

/** Sum entry hours per project; optional as-of date excludes later spent_date values (time travel). */
export function sumTimeEntryHoursByProject(
  entries: HarvestTimeEntryRow[],
  asOfIso?: string,
): Record<string, number> {
  const asOf = asOfIso ? parseIsoDate(asOfIso) : null;
  const totals: Record<string, number> = {};
  for (const entry of entries) {
    const projectId = nz(entry.project_harvest_id).trim();
    if (!projectId) continue;
    const spentDate = nz(entry.spent_date).trim();
    if (asOf) {
      const spentOn = parseIsoDate(spentDate);
      if (!spentOn || spentOn.getTime() > asOf.getTime()) continue;
    }
    totals[projectId] = (totals[projectId] || 0) + parseHoursValue(entry.hours);
  }
  return totals;
}

export type ScenarioWithBudgetRows = ScenarioWithStableIds & {
  harvest: ScenarioWithStableIds["harvest"] & { budget_rows?: unknown[] };
};

/**
 * Sets each project's budget_spent to opening balance + summed time entries (per project).
 * Clears budget_rows so per-project budget_spent drives the mock budget report.
 */
export function recalculateBudgetSpentFromTimeEntries<T extends ScenarioWithBudgetRows>(
  scenario: T,
  options?: { asOfIso?: string; timeEntries?: HarvestTimeEntryRow[] },
): T {
  const entries = options?.timeEntries ?? scenario.harvest.time_entries;
  const totals = sumTimeEntryHoursByProject(entries, options?.asOfIso);
  const projects = scenario.harvest.projects.map((p) => {
    const projectId = nz(p.id).trim();
    const entrySum = projectId ? (totals[projectId] || 0) : 0;
    const anchorRaw = nz(p.budget_spent_anchor).trim();
    const anchor = anchorRaw ? parseHoursValue(anchorRaw) : parseHoursValue(p.budget_spent);
    const spent = anchor + entrySum;
    const lockedAnchor = anchorRaw || nz(p.budget_spent).trim();
    return {
      ...p,
      budget_spent: formatBudgetSpentHours(spent),
      budget_spent_anchor: lockedAnchor,
    };
  });
  return {
    ...scenario,
    harvest: {
      ...scenario.harvest,
      projects,
      budget_rows: [],
    },
  } as T;
}

export function syncEmployeesFromHarvestUsers<T extends ScenarioEmployeesSlice>(prev: T): T {
  const employees = [ ...prev.bamboo.employees ];
  for (const u of prev.harvest.users) {
    const id = nz(u.id).trim();
    const email = nz(u.email).trim();
    if (!id || !email) continue;
    const idx = employees.findIndex((e) => emailsMatch(e.workEmail, email));
    const row: BambooEmployeeRow = {
      id,
      workEmail: email,
      displayName: [ u.first_name, u.last_name ].filter((x) => nz(x).trim()).join(" ").trim() || email,
      firstName: u.first_name,
      lastName: u.last_name,
    };
    if (idx >= 0) {
      employees[idx] = { ...employees[idx], ...row };
    } else {
      employees.push(row);
    }
  }
  return { ...prev, bamboo: { ...prev.bamboo, employees } };
}
